import { apiClient } from '~/api/client'
import type {
    CatalogBulkUpdateResult,
    CatalogCompleteness,
    CatalogImportResult,
    CatalogItem,
    CatalogItemField,
    CatalogItemInput,
    CatalogSummary,
    GenerateMissingResult,
    PartCount,
    RevisionApplyResult,
    RevisionDiff,
} from '~/api/types'

/** 일괄 편집에서 한 번에 바꿀 수 있는 필드(채점·모니터링에 직접 반영되는 값 포함). */
export type CatalogBulkPatch = Partial<
    Pick<
        CatalogItemInput,
        'classification' | 'scale' | 'assessmentType' | 'dataType'
    > & { isDeterministic: boolean }
>

// 카탈로그 읽기/쓰기 API. 평가 매뉴얼 카탈로그 요약·항목을 콘텐츠 운영 화면에서 소비하고,
// 매뉴얼 개정 시 superadmin이 항목을 직접 수정/추가한다.
// (벌크 재적재는 여전히 운영 CLI(datapipe) 책임이며 여기서는 단건 편집만 한다.)

/** 항목 목록·개수 공통 필터. 빈 값은 toQuery에서 자동 제외된다. */
export interface CatalogItemFilters {
    q?: string
    scale?: string
    classification?: string
    // PART 다중선택. 각 값은 반복 파라미터(part=A&part=B)로 직렬화된다.
    part?: string[]
    assessmentType?: string
    dataType?: string
    deterministic?: boolean
    industry?: string
}

export interface ListCatalogItemsParams extends CatalogItemFilters {
    limit?: number
    offset?: number
}

// 값은 단일(undefined/빈값 제외)이거나 배열(각 원소를 반복 파라미터로 append)이다.
function toQuery(
    params: Record<string, string | number | boolean | string[] | undefined>
): string {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
            for (const v of value) {
                if (v !== '') search.append(key, v)
            }
        } else if (value !== undefined && value !== '') {
            search.set(key, String(value))
        }
    }
    const qs = search.toString()
    return qs ? `?${qs}` : ''
}

// 목록·개수 공통 필터 → 쿼리 파라미터(undefined/빈값은 toQuery가 제외, part는 반복).
function filterParams(f: CatalogItemFilters) {
    return {
        q: f.q,
        scale: f.scale,
        classification: f.classification,
        part: f.part,
        assessmentType: f.assessmentType,
        dataType: f.dataType,
        deterministic: f.deterministic,
        industry: f.industry,
    }
}

/** GET /admin/api/content/catalog/summary — 카탈로그 집계. */
export function getCatalogSummary() {
    return apiClient.get<CatalogSummary>('/content/catalog/summary')
}

/** GET /admin/api/content/catalog/completeness — 질의 미작성 링크·수집필드 없는 항목 집계. */
export function getCompleteness() {
    return apiClient.get<CatalogCompleteness>('/content/catalog/completeness')
}

/** POST /admin/api/content/catalog/questions/generate-missing — 빈 사전 질의를 LLM으로 일괄 생성(superadmin).
 *  수 초~분 소요될 수 있다. limit으로 한 번에 생성할 건수를 제한할 수 있다(미지정 시 서버 기본값). */
export function generateMissingQuestions(limit?: number) {
    return apiClient.post<GenerateMissingResult>(
        '/content/catalog/questions/generate-missing',
        { limit }
    )
}

/** GET /admin/api/content/catalog/items — 항목 목록(검색·다중 필터, 페이지네이션). */
export function listCatalogItems(params: ListCatalogItemsParams = {}) {
    const query = toQuery({
        ...filterParams(params),
        limit: params.limit,
        offset: params.offset,
    })
    return apiClient.get<CatalogItem[]>(`/content/catalog/items${query}`)
}

/** GET /admin/api/content/catalog/items/count — 동일 필터 기준 전체 개수(페이지네이션용). */
export function countItems(filters: CatalogItemFilters = {}) {
    const query = toQuery(filterParams(filters))
    return apiClient.get<{ total: number }>(
        `/content/catalog/items/count${query}`
    )
}

/** GET /admin/api/content/catalog/items/{id} — 단건(전체 필드). 편집 드로어 진입 시 최신값 로드. */
export function getCatalogItem(id: string) {
    return apiClient.get<CatalogItem>(
        `/content/catalog/items/${encodeURIComponent(id)}`
    )
}

/** GET /admin/api/content/catalog/parts — PART별 항목 수(필터 칩). */
export function listParts() {
    return apiClient.get<PartCount[]>('/content/catalog/parts')
}

/** GET /admin/api/content/catalog/items/{id}/fields — 항목별 수집 데이터 필드 + 사전 질의.
 *  편집 드로어에서 운영자가 사전 질의를 검수·수정하기 위해 로드한다. */
export function getItemFields(itemId: string) {
    return apiClient.get<CatalogItemField[]>(
        `/content/catalog/items/${encodeURIComponent(itemId)}/fields`
    )
}

/** PUT /admin/api/content/catalog/items/{id}/fields/{fieldId} — 한 필드의 사전 질의(question) 수정. */
export function updateFieldQuestion(
    itemId: string,
    fieldId: string,
    question: string
) {
    return apiClient.put<CatalogItemField>(
        `/content/catalog/items/${encodeURIComponent(itemId)}/fields/${encodeURIComponent(fieldId)}`,
        { question }
    )
}

/** PUT /admin/api/content/catalog/items/{id} — 항목 수정(superadmin).
 *  maxScore/scoringLogic/isDeterministic 변경은 평가 채점·모니터링에 직접 반영된다. */
export function updateCatalogItem(id: string, body: CatalogItemInput) {
    return apiClient.put<CatalogItem>(
        `/content/catalog/items/${encodeURIComponent(id)}`,
        body
    )
}

/** POST /admin/api/content/catalog/items — 항목 추가(superadmin). id는 서버 생성. */
export function createCatalogItem(body: CatalogItemInput) {
    return apiClient.post<CatalogItem>('/content/catalog/items', body)
}

/** GET /admin/api/content/catalog/export — 전체 카탈로그를 .xlsx로 내려받는다(admin).
 *  첫 컬럼은 id(매칭 키). JWT가 필요해 blob fetch로 받고 임시 <a download>로 저장한다. */
export function exportCatalog() {
    return apiClient.downloadBlob(
        '/content/catalog/export',
        'haccp-catalog.xlsx'
    )
}

/** POST /admin/api/content/catalog/import — 편집한 .xlsx를 올려 id 매칭으로 반영(superadmin).
 *  id 있고 존재→수정, 비었거나 미존재→신규. 부분 실패 허용({row,id,error}). */
export function importCatalog(file: File) {
    const form = new FormData()
    form.append('file', file)
    return apiClient.postForm<CatalogImportResult>(
        '/content/catalog/import',
        form
    )
}

/** POST /admin/api/content/catalog/items/bulk-update — 선택한 ids 전체에 patch 한 필드를 적용(superadmin).
 *  단건 PUT을 여러 번 보내는 대신 서버에서 일괄 처리한다. 부분 실패는 {id,error}로 모인다.
 *  classification/scale/dataType/isDeterministic 변경은 평가 채점·모니터링에 직접 반영된다. */
export function bulkUpdateItems(ids: string[], patch: CatalogBulkPatch) {
    return apiClient.post<CatalogBulkUpdateResult>(
        '/content/catalog/items/bulk-update',
        { ids, patch }
    )
}

/** POST /admin/api/content/catalog/export — 선택한 ids만 .xlsx로 내려받는다(admin).
 *  본문에 ids가 필요해 POST 변형으로 받고, JWT 인증을 위해 blob fetch + 임시 <a download>로 저장한다. */
export function exportCatalogSelected(ids: string[]) {
    return apiClient.downloadBlob(
        '/content/catalog/export',
        'haccp-catalog-selected.xlsx',
        { ids }
    )
}

/** POST /admin/api/content/catalog/revision/diff — 새 평가매뉴얼 xlsx와 현행 카탈로그를
 *  비교한다(미리보기 전용·자동 반영 없음). multipart `file`=xlsx. */
export function diffRevision(file: File) {
    const form = new FormData()
    form.append('file', file)
    return apiClient.postForm<RevisionDiff>(
        '/content/catalog/revision/diff',
        form
    )
}

/** POST /admin/api/content/catalog/revision/apply — 검토 후 선택한 항목만 일괄 반영(superadmin).
 *  modified=수정 항목(id + 변경 필드), added=신규 항목(필드). 삭제는 안전상 자동 반영하지 않는다. */
export function applyRevision(body: {
    modified: ({ id: string } & Partial<CatalogItemInput>)[]
    added: Partial<CatalogItemInput>[]
}) {
    return apiClient.post<RevisionApplyResult>(
        '/content/catalog/revision/apply',
        body
    )
}
